/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Model from "@ember-data/model";
import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import dayjs from "dayjs";
import { task } from "ember-concurrency";
import type IntlService from "ember-intl/services/intl";
import { getPrescriptionJournalEventComponent } from "parog-web/components/prescription/utils/prescription-helpers";
import BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type JournalEvent from "parog-web/models/journal-event";
import MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import type NotificationTriggeredJournalEvent from "parog-web/models/notification-triggered-journal-event";
import ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type PatientChatMessageSentJournalEvent from "parog-web/models/patient-chat-message-sent-journal-event";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import type SemeiaSessionService from "parog-web/services/session";

export const ChatMessageInterlocutor = {
  Sender: "sender",
  Recipient: "recipient",
  Recipient_Alternate: "recipient-alternate"
};

interface ChatMessageFeedMessageComponentArgs {
  messageEvent: PatientChatMessageSentJournalEvent;
  lastMessage: boolean;
}

export default class ChatMessageFeedMessageComponent extends Component<ChatMessageFeedMessageComponentArgs> {
  @service declare session: SemeiaSessionService;
  @service declare intl: IntlService;
  @service declare store: Store;

  get message() {
    return this.args.messageEvent.body;
  }
  get creator() {
    return this.args.messageEvent.creator;
  }
  get creatorType() {
    return (this.creator?.constructor as typeof Model)?.modelName;
  }

  get isMessageSentByMedic() {
    return this.creatorType === "medic";
  }

  get isMessageSentByPatient() {
    return this.creatorType === "patient";
  }

  get interlocutorDisplayName(): string {
    if (this.interlocutorType === ChatMessageInterlocutor.Sender) {
      return this.intl.t("chat.interlocutors.sender");
    }

    if (this.isMessageSentByMedic) {
      const creator = this.creator as ModelRegistry["medic"];
      const prefix =
        creator.role === "doctor"
          ? this.intl.t("chat.interlocutors.doctor") + " "
          : "";
      return prefix + creator.fullName;
    } else if (this.isMessageSentByPatient) {
      return this.intl.t("chat.interlocutors.patient");
    }
    return this.intl.t("chat.interlocutors.program");
  }

  get readAtSignature(): string {
    return `Lu, le ${dayjs(this.args.messageEvent.readByPatientAt).format("DD/MM/YYYY")}`;
  }

  get shouldDisplayReadAtSignature(): boolean {
    return (
      !!this.session.medic &&
      this.args.messageEvent.readByPatientAt &&
      this.isMessageSentByMedic
    );
  }

  get signature(): string {
    return `${this.interlocutorDisplayName} le ${dayjs(
      this.args.messageEvent.occurredAt
    ).format("DD/MM/YYYY à HH:mm")}`;
  }

  get triggeringJournalEvent(): JournalEvent {
    return this.args.messageEvent.triggeringJournalEvent;
  }

  get isReadOnly(): boolean {
    return this.session.patient != null;
  }

  get callbackUrl(): string {
    const patientId = this.args.messageEvent.patient.get("id");
    return this.session.medic
      ? `/medics-web/patients/${patientId}/chat`
      : "/patient-web/chat";
  }

  get triggeringJournalEventMessage(): string {
    const date = dayjs(this.triggeringJournalEvent.occurredAt).format(
      "DD/MM/YYYY"
    );

    if (this.triggeringJournalEvent instanceof FileUploadedJournalEvent) {
      return `${date} - ${
        (this.triggeringJournalEvent as FileUploadedJournalEvent).title
      }`;
    }

    return date;
  }

  get triggeringJournalEventIsPrescription(): boolean {
    return (
      this.triggeringJournalEvent instanceof BioExamPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof MedicationPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof
        MedicalImagingPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof
        ParamedicalCarePrescribedJournalEvent
    );
  }

  get prescriptionComponent(): string | undefined {
    return getPrescriptionJournalEventComponent(
      this.triggeringJournalEvent as PrescriptionJournalEvent
    );
  }

  get prescriptionRoute(): string | undefined {
    if (this.session.medic) {
      return `medics-web.protected.patients.patient.prescriptions.${this.prescriptionComponent}`;
    }

    return `patient-web.protected.prescriptions.${this.prescriptionComponent}`;
  }

  get interlocutorType(): string {
    if (!this.creator) {
      return ChatMessageInterlocutor.Recipient;
    }

    if (
      this.isMessageSentByMedic &&
      this.session.medic?.id == this.creator.id
    ) {
      return ChatMessageInterlocutor.Sender;
    } else if (
      this.isMessageSentByPatient &&
      this.session.patient?.id == this.creator.id
    ) {
      return ChatMessageInterlocutor.Sender;
    } else if (this.isMessageSentByMedic && this.session.medic) {
      return ChatMessageInterlocutor.Recipient_Alternate;
    }
    return ChatMessageInterlocutor.Recipient;
  }

  get shouldShowUnreadMessage(): boolean {
    return (
      this.args.lastMessage &&
      !!this.session.medic &&
      !this.session.medic.disabledNotificationTypes?.includes(
        "PatientChatMessageSentJournalEvent"
      ) &&
      this.isMessageSentByPatient &&
      (this.args.messageEvent.notificationTriggeredJournalEvents.length == 0 ||
        (
          this.args.messageEvent
            .notificationTriggeredJournalEvents as NotificationTriggeredJournalEvent[]
        ).every(je => je.hasBeenProcessed))
    );
  }

  unreadMessage = task(async () => {
    await this.store
      .createRecord("notification-triggered-journal-event", {
        occurredAt: this.args.messageEvent.occurredAt,
        patient: this.args.messageEvent.patient,
        triggeringJournalEvent: this.args.messageEvent
      })
      .save();
  });
}
